import type { ApiResourceDescriptor } from "../resources/types";

function hasArrayProperty<TKey extends string>(
  value: unknown,
  key: TKey,
): value is Record<string, unknown> & Record<TKey, unknown[]> {
  return (
    value != null &&
    typeof value === "object" &&
    key in value &&
    Array.isArray((value as Record<string, unknown>)[key])
  );
}

function isDataPageCache(
  value: unknown,
): value is Record<string, unknown> & Record<"items", unknown[]> {
  return (
    hasArrayProperty(value, "items") &&
    typeof value.totalItemsCount === "number" &&
    typeof value.hasNextPage === "boolean"
  );
}

function isPagedReportCache(
  value: unknown,
): value is Record<string, unknown> & Record<"data", unknown[]> {
  return (
    hasArrayProperty(value, "data") &&
    typeof value.page === "number" &&
    typeof value.pageSize === "number" &&
    typeof value.hasNextPage === "boolean"
  );
}

function replaceEntityInArray(
  entities: unknown[],
  updated: unknown,
  updatedKey: string,
  getEntityKey: NonNullable<ApiResourceDescriptor["getEntityKey"]>,
) {
  let replaced = false;
  const next = entities.map((entity) => {
    if (getEntityKey(entity) !== updatedKey) return entity;
    replaced = true;
    return updated;
  });

  if (!replaced) return entities;
  return next;
}

export function replaceEntityInCollectionCache(
  cached: unknown,
  updated: unknown,
  descriptor: ApiResourceDescriptor,
) {
  const getEntityKey = descriptor.getEntityKey;
  if (!getEntityKey) return cached;

  const updatedKey = getEntityKey(updated);
  if (updatedKey == null) return cached;

  if (Array.isArray(cached)) {
    return replaceEntityInArray(cached, updated, updatedKey, getEntityKey);
  }

  if (isDataPageCache(cached)) {
    const items = replaceEntityInArray(
      cached.items,
      updated,
      updatedKey,
      getEntityKey,
    );
    if (items === cached.items) return cached;
    return { ...cached, items };
  }

  if (isPagedReportCache(cached)) {
    const data = replaceEntityInArray(
      cached.data,
      updated,
      updatedKey,
      getEntityKey,
    );
    if (data === cached.data) return cached;
    return { ...cached, data };
  }

  return cached;
}
